"use client"

import { Brain, Camera, Fish, MapPin } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect } from "react"
import { Logo } from "@/components/logo"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { createClient } from "@/lib/supabase/client"

export default function HomePage() {
  const supabase = createClient()

  const router = useRouter()

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        router.replace("/dashboard")
      }
    })
  }, [router])
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <Logo width={400} height={100} className="h-20 w-auto" />
          </div>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            A private, invitation-only fishing journal powered by AI. Track your catches with intelligent species
            identification and automatic location tracking.
          </p>
          <div className="space-x-4">
            <Link href="/login">
              <Button size="lg">Sign In</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Everything you need to track your catches.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Camera className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>Photo Upload</CardTitle>
                <CardDescription>Upload photos of your catches with automatic metadata extraction</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  Automatically extract date, time, and GPS coordinates from your photos
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Brain className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>AI Fish ID</CardTitle>
                <CardDescription>Powered by advanced AI to identify fish species</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">Get instant species identification with confidence scores</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <MapPin className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>Location Tracking</CardTitle>
                <CardDescription>Track where you caught each fish automatically</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">Automatic location names from GPS coordinates in your photos</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Private Access Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Exclusive Access</h2>
          <p className="text-xl text-gray-300 mb-8">Ainglr is private and access is by invitation only.</p>
          <p className="text-lg text-gray-400 mb-8">Already have an invitation code?</p>
          <div className="space-x-4">
            <Link href="/signup">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white hover:text-black"
              >
                Join with Invitation Code
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
